import { PO_STATUS_VALUES } from "@erp/shared";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { useCallback } from "react";
import { z } from "zod";
import { Badge } from "~/components/ui/badge";
import { buttonVariants } from "~/components/ui/button";
import { DataList, type Column } from "~/components/ui/data-list";
import { Page, PageHeader, Panel, SearchInput, Segmented, Toolbar } from "~/components/ui/page";
import { Select } from "~/components/ui/select";
import { EmptyState, ErrorState } from "~/components/ui/states";
import { isOverdue, PURCHASE_TONE } from "~/features/purchasing/model";
import { suppliersForSelectQuery } from "~/features/products/product";
import { PurchaseDocuments } from "~/features/purchasing/documents";
import { Payables } from "~/features/purchasing/payables";
import { Suggestions } from "~/features/purchasing/suggestions";
import { ReplenishBadge } from "~/features/purchasing/replenish";
import { api, unwrap } from "~/lib/api";
import { cn } from "~/lib/cn";
import { useFormat, useT } from "~/lib/i18n";
import { useCan } from "~/lib/session";
import { useInfiniteList } from "~/lib/use-list";

const STATUS_FILTERS = ["open", "auto", ...PO_STATUS_VALUES, "archived", "all"] as const;
const ALL_SUPPLIERS = "__all";

const search = z.object({
  view: z.enum(["orders", "suggestions", "payables", "documents"]).optional(),
  q: z.string().optional(),
  status: z.enum(STATUS_FILTERS).optional(),
  supplierId: z.uuid().optional(),
});
type Search = z.infer<typeof search>;

export const Route = createFileRoute("/_app/purchasing/")({ validateSearch: search, component: Purchasing });

function fetchOrders(params: Omit<Search, "view"> & { offset: number; limit: number }) {
  const { status = "open", ...rest } = params;
  // « Réappro auto »: the living replenishment drafts only.
  if (status === "auto") return unwrap(api.purchasing.orders.get({ query: { ...rest, auto: "1" } }));
  return unwrap(api.purchasing.orders.get({ query: { ...rest, status: status === "all" ? undefined : status } }));
}
type Row = Awaited<ReturnType<typeof fetchOrders>>["items"][number];

function Progress({ row }: { row: Pick<Row, "ordered" | "received"> }) {
  const t = useT();
  return (
    <span className="flex items-center gap-2">
      <progress
        value={row.received}
        max={Math.max(row.ordered, 1)}
        className="h-1.5 w-16 appearance-none overflow-hidden rounded-full bg-sunken [&::-moz-progress-bar]:bg-gold [&::-webkit-progress-bar]:bg-sunken [&::-webkit-progress-value]:rounded-full [&::-webkit-progress-value]:bg-gold"
        aria-label={t("purchasing.fields.progress")}
      />
      <span className="text-xs text-fg-2 tabular">
        {t("purchasing.fields.progressValue", { received: row.received, ordered: row.ordered })}
      </span>
    </span>
  );
}

function Purchasing() {
  const t = useT();
  const can = useCan();
  const navigate = useNavigate({ from: Route.fullPath });
  const filters = Route.useSearch();
  const view = filters.view ?? "orders";
  const setSearch = useCallback(
    (patch: Partial<Search>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );

  return (
    <Page>
      <PageHeader
        title={t("purchasing.title")}
        actions={
          can("purchasing:write") ? (
            <Link to="/purchasing/new" className={buttonVariants({ variant: "primary" })}>
              <Plus />
              {t("purchasing.new")}
            </Link>
          ) : null
        }
      />
      <div className="mb-4">
        <Segmented
          label={t("purchasing.title")}
          value={view}
          onChange={(v) => setSearch({ view: v === "orders" ? undefined : v })}
          options={[
            { value: "orders", label: t("purchasing.views.orders") },
            { value: "suggestions", label: t("purchasing.views.suggestions") },
            { value: "payables", label: t("purchasing.views.payables") },
            { value: "documents", label: t("purchasing.views.documents") },
          ]}
        />
      </div>
      {view === "suggestions" ? (
        <Suggestions />
      ) : view === "payables" ? (
        <Payables />
      ) : view === "documents" ? (
        <PurchaseDocuments />
      ) : (
        <OrderList filters={filters} setSearch={setSearch} />
      )}
    </Page>
  );
}

function OrderList({ filters, setSearch }: { filters: Search; setSearch: (patch: Partial<Search>) => void }) {
  const t = useT();
  const suppliers = useQuery(suppliersForSelectQuery);
  const format = useFormat();
  const navigate = useNavigate();
  const { view: _view, ...params } = filters;
  const list = useInfiniteList("purchasing", params, fetchOrders);
  const status = filters.status ?? "open";

  const statusLabel = (s: (typeof STATUS_FILTERS)[number]) =>
    s === "open" || s === "all" || s === "auto" || s === "archived"
      ? t(`purchasing.statusFilter.${s}`)
      : t(`purchasing.statuses.${s}`);

  const columns: Column<Row>[] = [
    { id: "number", header: t("purchasing.fields.number"), cell: (r) => <span className="font-medium tabular">{r.number}</span> },
    {
      id: "supplier",
      header: t("purchasing.fields.supplier"),
      cell: (r) => <span className="truncate">{r.supplierName}</span>,
      className: "max-w-[260px]",
    },
    {
      id: "status",
      header: t("common.fields.status"),
      cell: (r) => (
        <span className="inline-flex flex-wrap items-center gap-1">
          <Badge tone={PURCHASE_TONE[r.status]}>{t(`purchasing.statuses.${r.status}`)}</Badge>
          <ReplenishBadge order={r} />
        </span>
      ),
    },
    { id: "progress", header: t("purchasing.fields.progress"), cell: (r) => <Progress row={r} />, minWidth: "lg" },
    {
      id: "expected",
      header: t("purchasing.fields.expectedAt"),
      cell: (r) =>
        r.expectedAt ? (
          <span className={cn(isOverdue(r) ? "text-danger" : "text-fg-2")}>{format.date(r.expectedAt, "short")}</span>
        ) : null,
    },
    {
      id: "paid",
      header: t("purchasing.fields.paid"),
      align: "end",
      cell: (r) => format.money(r.paidCents, r.currency),
      minWidth: "xl",
    },
    { id: "total", header: t("purchasing.fields.total"), align: "end", cell: (r) => format.money(r.totalCents, r.currency) },
  ];

  return (
    <Panel>
      <Toolbar>
        <SearchInput
          value={filters.q ?? ""}
          onChange={(q) => setSearch({ q: q || undefined })}
          placeholder={t("purchasing.searchPlaceholder")}
        />
        <div className="w-full sm:ml-auto sm:w-56">
          <Select
            aria-label={t("purchasing.fields.supplier")}
            value={filters.supplierId ?? ALL_SUPPLIERS}
            onValueChange={(v) => setSearch({ supplierId: v === ALL_SUPPLIERS ? undefined : v })}
            options={[
              { value: ALL_SUPPLIERS, label: t("purchasing.allSuppliers") },
              ...(suppliers.data?.items ?? []).map((s) => ({ value: s.id, label: s.name })),
            ]}
          />
        </div>
        <div className="w-full sm:w-52">
          <Select
            aria-label={t("common.fields.status")}
            value={status}
            onValueChange={(v) => setSearch({ status: v === "open" ? undefined : v })}
            options={STATUS_FILTERS.map((s) => ({ value: s, label: statusLabel(s) }))}
          />
        </div>
      </Toolbar>
      {list.isError ? (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      ) : (
        <DataList
          rows={list.rows}
          rowKey={(r) => r.id}
          columns={columns}
          card={(r) => (
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate font-medium">{r.supplierName}</p>
                <p className="text-xs text-fg-3 tabular">
                  {r.number}
                  {r.expectedAt ? ` — ${format.date(r.expectedAt, "short")}` : ""}
                </p>
                <div className="mt-1.5">
                  <Progress row={r} />
                </div>
              </div>
              <div className="shrink-0 text-right">
                <p className="tabular">{format.money(r.totalCents, r.currency)}</p>
                <Badge tone={PURCHASE_TONE[r.status]}>{t(`purchasing.statuses.${r.status}`)}</Badge>
                <ReplenishBadge order={r} />
              </div>
            </div>
          )}
          onOpen={(r) => void navigate({ to: "/purchasing/$id", params: { id: r.id } })}
          sort={{ dir: "desc" }}
          onSortChange={() => undefined}
          loading={list.isLoading}
          hasMore={list.hasMore}
          loadingMore={list.loadingMore}
          onLoadMore={list.loadMore}
          isDimmed={(r) => r.status === "cancelled" || r.status === "closed"}
          empty={
            !filters.q && status === "open" ? (
              <EmptyState title={t("purchasing.emptyTitle")} body={t("purchasing.emptyBody")} />
            ) : (
              <EmptyState title={t("common.states.noResults")} body={t("common.states.noResultsHint")} />
            )
          }
        />
      )}
    </Panel>
  );
}
